import { z } from "zod"
import { RegistrationRoleEnum } from "@pathwright/graphql-client/src/generated"

// The items that can be created or selected.
export type ItemKey = "resource" | "cohort"

// The selected types represent those objects that already exist.
export type SelectedResource = z.infer<typeof selectedResourceSchema>
export type SelectedCohort = z.infer<typeof selectedCohortSchema>

// The new types represet those objects that don't exist.
export type NewResource = z.infer<typeof newResourceSchema>
export type NewCohort = z.infer<typeof newCohortSchema>

// Role can either be a registration role or null.
export type SelectedResourceRole = z.infer<typeof resourceRoleSchema> | null
export type SelectedCohortRole = z.infer<typeof cohortRoleSchema> | null

// The items (Resource and Cohort) will either by form values for
// to-be-created objects, or the existing (selected) objects themselevs.
export type Resource = z.infer<typeof resourceSchema>
export type Cohort = z.infer<typeof cohortSchema>
// Unsure if "Path" should be used for "Resource", but for consistency
// at least with the Resource and Cohort types above, mapping the role
// terms to those makes sense.
export type ResourceRole = SelectedResourceRole
export type CohortRole = SelectedCohortRole

export type PathCreatorValidValues = z.infer<
  typeof pathCreatorValidValuesSchema
>
export type PathCreatorRawValues = z.infer<
  typeof pathCreatorRawValuesSchema
> | null
export type PathCreatorResult = z.infer<typeof pathCreatorResultSchema>

// We want to conform an enum to our own RegistrationRoleEnum and
// using z.nativeEnum is how to do that.
const registrationRoleSchema = z.nativeEnum(RegistrationRoleEnum)

const resourceRoleEnum = z.enum([registrationRoleSchema.enum.Editor])

const resourceRoleSchema = z.union([resourceRoleEnum, z.null()])

const newResourceSchema = z.object({
  name: z.string().min(1),
  image: z.string().url(),
  role: resourceRoleSchema.optional()
})

// The selected resource differs in that it has an id field + sourceCohort
// that includes an optional role.
const selectedResourceSchema = newResourceSchema.merge(
  z.object({
    id: z.number(),
    background_image: z.union([z.string(), z.null()]).optional(),
    sourceCohort: z.object({
      id: z.number(),
      role: resourceRoleSchema.optional()
    })
  })
)

// Order here is important, from greatest constraint to least.
const resourceSchema = z.union([selectedResourceSchema, newResourceSchema])

const cohortRoleEnum = z.enum([
  registrationRoleSchema.enum.Teacher,
  registrationRoleSchema.enum.Moderator,
  registrationRoleSchema.enum.Student
])

const cohortRoleSchema = z.union([cohortRoleEnum, z.null()])

const newCohortSchema = z.object({
  name: z.string().min(1),
  role: cohortRoleSchema.optional()
})

// The selected cohort differs only in that it has an id field.
const selectedCohortSchema = newCohortSchema.merge(
  z.object({
    id: z.number(),
    // NOTE: having to include the registrations list expected by <GroupAvatars />
    registrations: z.any().optional()
  })
)

// Order here is important, from greatest constraint to least.
const cohortSchema = z.union([selectedCohortSchema, newCohortSchema])

// User must submit wth either a resource role or cohort role selected.
export const pathCreatorValidValuesSchema = z.union([
  z.object({
    resource: resourceSchema.and(
      z.object({
        role: resourceRoleEnum
      })
    ),
    cohort: cohortSchema
  }),
  z.object({
    resource: resourceSchema,
    cohort: cohortSchema.and(
      z.object({
        role: cohortRoleEnum
      })
    )
  })
])

// Superset of valid values, allowing for nulls and unselected roles.
export const pathCreatorRawValuesSchema = pathCreatorValidValuesSchema.or(
  z.object({
    resource: resourceSchema.or(z.null()),
    cohort: cohortSchema.or(z.null())
  })
)

export const pathCreatorResultSchema = z.object({
  resource: selectedResourceSchema,
  cohort: selectedCohortSchema
})

// May want a different location for this.
// Check if item (cohort or resource) is an "existing" item, meaning it has an id
// which is not -1.
export function isExistingItem(
  item: Resource | Cohort | null
): item is SelectedResource | SelectedCohort {
  // Get the result of parsing the item as a resource or cohort,
  // preferring the first one that succeeds.
  const resourceResult = selectedResourceSchema.safeParse(item)
  const cohortResult = selectedCohortSchema.safeParse(item)
  const result = resourceResult.success ? resourceResult : cohortResult
  return result.success && result.data.id !== -1
}

// Check if item (cohort or resource) is a "new" item, meaning it has an id
// which is -1.
export function isNewItem(item: Resource | Cohort | null): boolean {
  return !!item && "id" in item && item.id === -1 && !isExistingItem(item)
}
